import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { EnvironmentService } from '@nomoh-monorepo/shared-data-access-environment';
import { IServico } from '@nomoh-monorepo/shared-types';
import { firstValueFrom } from 'rxjs';
import { TipoServicoDataService } from '../feature-tipo-servico/tipo-servico.dataservice';
import { ServicoDataService } from './servico.dataservice';

/**
 * Service HTTP para Servico
 */
@Injectable({
  providedIn: 'root',
})
export class ServicoService {
  private http = inject(HttpClient);
  private env = inject(EnvironmentService);
  private dataService = inject(ServicoDataService);
  private tipoServicoDataService = inject(TipoServicoDataService);

  private get baseUrl(): string {
    return `${this.env.getApiUrl('bardo')}/servico`;
  }

  async findAll(filtro?: unknown): Promise<IServico[]> {
    try {
      this.dataService.setLoading(true);
      this.dataService.limparErro();

      const servicos = await firstValueFrom(
        this.http.post<IServico[]>(`${this.baseUrl}/findAll`, filtro || {})
      );

      const normalizados = servicos.map((servico) =>
        this.normalizarDuracaoCliente(servico)
      );

      this.dataService.setServicos(normalizados);
      normalizados.forEach((servico) =>
        this.tipoServicoDataService.upsertServico(servico)
      );
      return normalizados;
    } catch (error) {
      const mensagem =
        (error as { error?: { message?: string } })?.error?.message ||
        'Erro ao buscar serviços';
      this.dataService.setErro(mensagem);
      throw error;
    } finally {
      this.dataService.setLoading(false);
    }
  }

  async findById(id: string): Promise<IServico> {
    try {
      this.dataService.setLoading(true);
      this.dataService.limparErro();

      const servico = await firstValueFrom(
        this.http.get<IServico>(`${this.baseUrl}/findById`, {
          params: { id: id.toString() },
        })
      );

      const normalizado = this.normalizarDuracaoCliente(servico);
      this.dataService.setServicoAtual(normalizado);
      this.tipoServicoDataService.upsertServico(normalizado);
      return normalizado;
    } catch (error) {
      const mensagem =
        (error as { error?: { message?: string } })?.error?.message ||
        'Erro ao buscar serviço';
      this.dataService.setErro(mensagem);
      throw error;
    } finally {
      this.dataService.setLoading(false);
    }
  }

  async criar(servico: Partial<IServico>): Promise<string> {
    try {
      this.dataService.setLoading(true);
      this.dataService.limparErro();

      const id = await firstValueFrom(
        this.http.post<string>(`${this.baseUrl}/create`, servico)
      );

      if (id) {
        const criado = this.normalizarDuracaoCliente({
          ...(servico as IServico),
          id,
        });
        this.dataService.addServico(criado);
        this.tipoServicoDataService.upsertServico(criado);
      }

      return id;
    } catch (error) {
      const mensagem =
        (error as { error?: { message?: string } })?.error?.message ||
        'Erro ao criar serviço';
      this.dataService.setErro(mensagem);
      throw error;
    } finally {
      this.dataService.setLoading(false);
    }
  }

  async criarMultiplo(servicos: Partial<IServico>[]): Promise<string[]> {
    try {
      this.dataService.setLoading(true);
      this.dataService.limparErro();

      const ids = await firstValueFrom(
        this.http.post<string[]>(`${this.baseUrl}/createMultiple`, servicos)
      );

      if (ids?.length) {
        ids.forEach((id, index) => {
          const servico = servicos[index];
          if (servico) {
            const criado = this.normalizarDuracaoCliente({
              ...(servico as IServico),
              id,
            });
            this.dataService.addServico(criado);
            this.tipoServicoDataService.upsertServico(criado);
          }
        });
      } else {
        await this.findAll();
      }
      return ids;
    } catch (error) {
      const mensagem =
        (error as { error?: { message?: string } })?.error?.message ||
        'Erro ao criar serviços';
      this.dataService.setErro(mensagem);
      throw error;
    } finally {
      this.dataService.setLoading(false);
    }
  }

  async atualizar(id: string, servico: Partial<IServico>): Promise<void> {
    try {
      this.dataService.setLoading(true);
      this.dataService.limparErro();

      await firstValueFrom(
        this.http.put<void>(`${this.baseUrl}/update`, {
          ...servico,
          id,
        })
      );

      const atualizado = {
        ...(servico as IServico),
        id,
      } as IServico;
      const normalizado = this.normalizarDuracaoCliente(atualizado);
      this.dataService.updateServico(id, normalizado);
      this.tipoServicoDataService.upsertServico(normalizado);
    } catch (error) {
      const mensagem =
        (error as { error?: { message?: string } })?.error?.message ||
        'Erro ao atualizar serviço';
      this.dataService.setErro(mensagem);
      throw error;
    } finally {
      this.dataService.setLoading(false);
    }
  }

  async deletar(id: string): Promise<void> {
    try {
      this.dataService.setLoading(true);
      this.dataService.limparErro();

      await firstValueFrom(
        this.http.delete<boolean>(`${this.baseUrl}/delete`, {
          params: { id: id.toString() },
        })
      );

      this.dataService.removeServico(id);
      this.tipoServicoDataService.removeServico(id);
    } catch (error) {
      const mensagem =
        (error as { error?: { message?: string } })?.error?.message ||
        'Erro ao deletar serviço';
      this.dataService.setErro(mensagem);
      throw error;
    } finally {
      this.dataService.setLoading(false);
    }
  }

  async ativar(id: string): Promise<void> {
    await this.atualizar(id, { status: true });
  }

  async desativar(id: string): Promise<void> {
    await this.atualizar(id, { status: false });
  }

  // ==================== NORMALIZAÇÃO ====================

  private normalizarDuracaoCliente(servico: IServico): IServico {
    const valorDuracao = (servico as IServico & { duracao: unknown }).duracao;
    return {
      ...servico,
      duracao: this.converterDuracaoParaMinutos(valorDuracao),
    } as IServico;
  }

  private converterDuracaoParaMinutos(valor: unknown): number {
    if (typeof valor === 'number' && Number.isFinite(valor)) {
      return valor;
    }

    if (valor instanceof Date) {
      return valor.getUTCHours() * 60 + valor.getUTCMinutes();
    }

    if (typeof valor === 'string') {
      const trimmed = valor.trim();
      if (!trimmed) {
        return 0;
      }

      const numeric = Number(trimmed);
      if (Number.isFinite(numeric)) {
        return numeric;
      }

      if (trimmed.includes('T')) {
        const data = new Date(trimmed);
        if (!Number.isNaN(data.getTime())) {
          return data.getUTCHours() * 60 + data.getUTCMinutes();
        }
      }

      const partes = trimmed.split(':');
      if (partes.length >= 2) {
        const horas = Number(partes[0]);
        const minutos = Number(partes[1]);
        if (Number.isFinite(horas) && Number.isFinite(minutos)) {
          return horas * 60 + minutos;
        }
      }
    }

    return 0;
  }
}
